import { saveAs } from 'file-saver';

import { isDescendantOf, isRowCollapsed } from 'utils/grid';
import { exportCellFormatter } from 'utils/generic';
import { CellIds, ColumnTypes, DataTypes } from 'constants/grid';
import settings from 'reducers/settings';

function getCellData(data, colDef, settings) {
  let value = data[colDef.field];
  if (colDef.valueGetter) {
    value = colDef.valueGetter({ value, data });
  }

  const isNumeric = colDef.type === ColumnTypes.numeric;

  const cellFormatter = exportCellFormatter(settings);
  value = cellFormatter({ value });

  return {
    data: {
      type: isNumeric ? DataTypes.number : DataTypes.string,
      value,
    },
    styleId: isNumeric ? CellIds.numeric : CellIds.default,
  };
}

function getRowData(row, columns, settings) {
  return columns.map(({ colDef }) => getCellData(row.data, colDef, settings));
}

function getDisplayedColumns(columnApi) {
  return columnApi.getAllGridColumns().filter((c) => c.isVisible());
}

function getDisplayedColumnGroups(columnApi) {
  const columns = getDisplayedColumns(columnApi);

  return columns.reduce((res, col) => {
    if (col.parent) {
      const alreadyTaken =
        res.length &&
        res[res.length - 1].groupId === col.parent.originalColumnGroup.groupId;
      if (!alreadyTaken) {
        res.push(col.parent.originalColumnGroup);
      }
    }

    return res;
  }, []);
}

export function getSubgroupsData(
  { node: selectedNode, api, columnApi },
  settings
) {
  if (!selectedNode) {
    return [];
  }

  const subgroupsRows = [];
  api.forEachNode((node) => {
    const notRoot = node.level > 0;
    const isChild = node.parent.key === selectedNode.key;
    const isGroup = !!node.data.group;
    if (notRoot && isChild && isGroup) {
      subgroupsRows.push(node);
    }
  });

  const columns = getDisplayedColumns(columnApi);
  const columnGroups = getDisplayedColumnGroups(columnApi);

  const preheader = columnGroups.map((cg) => {
    const visibleChildrenLength = cg.children.filter((child) => child.visible)
      .length;
    return {
      data: {
        type: DataTypes.string,
        value: (cg.colGroupDef && cg.colGroupDef.headerName) || '',
      },
      mergeAcross: visibleChildrenLength - 1,
      styleId: CellIds.header,
    };
  });
  const header = columns.map((col) => ({
    data: {
      type: DataTypes.string,
      value: col.colDef.headerName || '',
    },
    styleId: CellIds.header,
  }));

  const data = subgroupsRows.map((row) => getRowData(row, columns, settings));
  const total = getRowData(selectedNode, columns, settings);
  total[0].data.value = `TOTAL ${total[0].data.value}`;

  return [preheader, header, ...data, [], total, [], [], [], []];
}

const groupsExportDef = {
  columnGroups: true,
  fileName: 'Groups listing report',
  shouldRowBeSkipped: ({ node }) => isRowCollapsed(node),
  processCellCallback: ({ value, node, column }) => {
    if (column.colId === 'ag-Grid-AutoColumn') {
      const indent = '   '.repeat(node.level);
      return `${indent}${value}`;
    }

    return value;
  },
};

function getGroupLeadScheduleReportInExcelXml({
  params,
  settings,
  selectedGroup,
  exportMode,
  sheetName,
  fileName,
}) {
  return params.api.getDataAsExcel({
    ...groupsExportDef,
    exportMode,
    sheetName: shortenSheetNameIfTooLong(sheetName),
    fileName,
    customHeader: getSubgroupsData(
      {
        ...params,
        node: selectedGroup,
      },
      settings
    ),
    shouldRowBeSkipped: ({ node }) => !isDescendantOf(selectedGroup, node),
  });
}

// MS Excel truncates sheet names if they are too long
function shortenSheetNameIfTooLong(sheetName) {
  const xmlSheetNameMaxLength = 29;
  return sheetName.length > xmlSheetNameMaxLength
    ? sheetName.substring(0, xmlSheetNameMaxLength)
    : sheetName;
}

export function exportLeadScheduleReportWithMultipleGroups(
  params,
  settings,
  groupIds
) {
  const selectedGroups = getGroupsNodes(params, groupIds);

  const hasSelectedGroups = selectedGroups && selectedGroups.length;
  if (!hasSelectedGroups) {
    return '';
  }

  const firstSelectedGroup = selectedGroups[0];
  const firstExcelOutput = getGroupLeadScheduleReportInExcelXml({
    params: params,
    settings,
    selectedGroup: firstSelectedGroup,
    exportMode: 'xml',
    sheetName: firstSelectedGroup.data.description,
    fileName: 'Lead schedule report',
  });

  const secondAndOtherGroups = selectedGroups.slice(1);
  // DOMParser was not used here because it produced parsing errors on ag-grid output
  const worksheetTags = getWorksheetTagsOfGroups(params, secondAndOtherGroups);
  const finalExcelOutput = getExcelXmlWithAddedWorksheets(
    firstExcelOutput,
    worksheetTags
  );

  const blob = new Blob([finalExcelOutput], { type: 'text/xml' });
  saveAs(blob, 'Lead schedule report.xml');
}

function getWorksheetTagsOfGroups(params, groups) {
  const worksheetTags = [];

  groups.forEach((group) => {
    const selectedGroupExcelOutput = getGroupLeadScheduleReportInExcelXml({
      params,
      settings,
      selectedGroup: group,
      exportMode: 'xml',
      sheetName: group.data.description,
      fileName: 'Lead schedule report',
    });

    const groupWorksheetTags = getWorksheetTags(selectedGroupExcelOutput);
    worksheetTags.push(...groupWorksheetTags);
  });

  return worksheetTags;
}

function getExcelXmlWithAddedWorksheets(excelXml, worksheetTags) {
  let result = `${excelXml}`;

  worksheetTags.forEach((worksheetTag) => {
    result = getExcelXmlWithAddedWorksheet(result, worksheetTag);
  });

  return result;
}

function getExcelXmlWithAddedWorksheet(excelXml, worksheetTag) {
  const workbookEndRegex = /<\/Workbook>/gi;
  const workbookEndTag = '</Workbook>';
  const workbookEndIndex = workbookEndRegex.exec(excelXml).index;

  return `${excelXml.substring(
    0,
    workbookEndIndex
  )}${worksheetTag}${workbookEndTag}`;
}

function getWorksheetTags(excelXml) {
  let tags = [];

  const startTagRegex = /<Worksheet ss:Name/gi;
  const endTagRegex = /<\/Worksheet>/gi;
  const worksheetEndIndex = 13;

  const startTagIndices = findIndicesOfSubstring(excelXml, startTagRegex);
  const endTagIndices = findIndicesOfSubstring(excelXml, endTagRegex);

  if (startTagIndices.length !== endTagIndices.length) {
    tags = [];
  }

  startTagIndices.forEach((startTagIndex, arrayIndex) => {
    const endTagIndex = endTagIndices[arrayIndex];
    const tag = excelXml.substring(
      startTagIndex,
      endTagIndex + worksheetEndIndex
    );

    tags.push(tag);
  });

  return tags;
}

function findIndicesOfSubstring(str, regex) {
  const indices = [];

  let result = '';
  while ((result = regex.exec(str))) {
    indices.push(result.index);
  }

  return indices;
}

export function getSelectedGroupsIds(params) {
  return getSelectedGroups(params)
    .filter((node) => !!node.data.group)
    .map((node) => node.data.id);
}

export function getSelectedGroups(params) {
  return params.api.getSelectedNodes().filter((node) => !!node.data.group);
}

export function getGroupsNodes(params, groupIds) {
  const nodes = [];
  const hasGroupIds = groupIds && groupIds.length;

  params.api.forEachNode((node) => {
    const notRoot = node.level > 0;
    const isGroup = !!node.data.group;
    const isInGroupIds = hasGroupIds && groupIds.includes(node.data.id);

    if (notRoot && isGroup && isInGroupIds) {
      nodes.push(node);
    }
  });

  return nodes;
}
